import { IconoirCheck, IconoirCopy } from "@ey-xd/motif-icon";
import { MotifIcon, MotifIconButton } from "@ey-xd/motif-react";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ApprovalStatusBadge from "./ApprovalStatusBadge";
import {
  ApprovalStage,
  getApprovalStageLabel,
  getRejectionRoleLabel,
  resolveApprovalStatus,
} from "./status";
import "./ApprovalStatusPopover.scss";

export interface ApprovalStatusApprover {
  Email?: string;
  Name?: string;
}
export interface RejectionReasonProps {
  Email?: string;
  Name?: string;
  OrderSkuId?: string;
  RejectReason?: string;
  RoleCd?: string;
  SkuId?: string;
  Status?: string;
}

interface ApprovalStatusPopoverProps {
  stage: ApprovalStage;
  rawStatus: string | null | undefined;
  approvers?: ApprovalStatusApprover[] | null;
  RejectionReasonInfo?: RejectionReasonProps | null;
  isAutoProvisioned?: boolean;
}

const CLOSE_DELAY_MS = 150;
const COPIED_FEEDBACK_MS = 2000;
const POPOVER_OPEN_EVENT = "approval-status-popover-open";
type PopoverPosition = CSSProperties & {
  "--approval-status-popover-arrow-top": string;
};

const getUniqueEmails = (approvers: ApprovalStatusApprover[]) => [
  ...new Set(approvers.map(({ Email }) => Email?.trim()).filter(Boolean)),
];

const ApprovalStatusPopover = ({
  stage,
  rawStatus,
  approvers,
  RejectionReasonInfo,
  isAutoProvisioned = false,
}: ApprovalStatusPopoverProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition>({
    left: -9999,
    top: -9999,
    "--approval-status-popover-arrow-top": "50%",
  });
  const tooltipId = useId();
  const resolvedStatus = resolveApprovalStatus(
    rawStatus,
    stage,
    isAutoProvisioned,
  );
  const approverLabel = getApprovalStageLabel(stage);
  const visibleApprovers = approvers || [];
  const allEmails = getUniqueEmails(visibleApprovers);
  const showRejection =
    resolvedStatus.status === "rejected" &&
    Boolean(RejectionReasonInfo?.RejectReason?.trim());

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = () => {
    clearCloseTimer();
    window.dispatchEvent(
      new CustomEvent(POPOVER_OPEN_EVENT, { detail: tooltipId }),
    );
    setIsOpen(true);
  };

  const closePopover = useCallback(() => {
    clearCloseTimer();
    setIsOpen(false);
  }, []);

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      const activeElement = document.activeElement;
      const containsFocus =
        triggerRef.current?.contains(activeElement) ||
        popoverRef.current?.contains(activeElement);

      if (!containsFocus) {
        setIsOpen(false);
      }
    }, CLOSE_DELAY_MS);
  };

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(
        () => setCopiedKey(null),
        COPIED_FEEDBACK_MS,
      );
    } catch {
      setCopiedKey(null);
    }
  };

  useEffect(() => {
    return () => {
      clearCloseTimer();
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const closeWhenAnotherPopoverOpens = (event: Event) => {
      const { detail } = event as CustomEvent<string>;
      if (detail !== tooltipId) closePopover();
    };

    window.addEventListener(POPOVER_OPEN_EVENT, closeWhenAnotherPopoverOpens);
    return () =>
      window.removeEventListener(
        POPOVER_OPEN_EVENT,
        closeWhenAnotherPopoverOpens,
      );
  }, [closePopover, tooltipId]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        closePopover();
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [closePopover, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) return;

      const triggerRect = trigger.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const horizontalPadding = 8;
      const popoverGap = 12;
      const top = Math.min(
        Math.max(
          horizontalPadding,
          triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2,
        ),
        window.innerHeight - popoverRect.height - horizontalPadding,
      );
      const left = Math.max(
        horizontalPadding,
        triggerRect.left - popoverRect.width - popoverGap,
      );

      const arrowTop = Math.min(
        Math.max(16, triggerRect.top + triggerRect.height / 2 - top),
        popoverRect.height - 16,
      );

      setPopoverPosition({
        left,
        top,
        "--approval-status-popover-arrow-top": `${arrowTop}px`,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, showRejection, visibleApprovers.length]);

  return (
    <>
      <ApprovalStatusBadge
        triggerRef={triggerRef}
        label={resolvedStatus.label}
        status={resolvedStatus.status}
        isExpanded={isOpen}
        tooltipId={isOpen ? tooltipId : undefined}
        onBlur={scheduleClose}
        onClick={openPopover}
        onFocus={openPopover}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            closePopover();
            triggerRef.current?.focus();
          }
        }}
        onPointerEnter={openPopover}
        onPointerLeave={scheduleClose}
      />

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            id={tooltipId}
            className={`approval-status-popover${showRejection ? " approval-status-popover--rejection" : ""}`}
            role="dialog"
            aria-label={
              showRejection
                ? `${approverLabel} rejection reason`
                : `${approverLabel} approvers`
            }
            style={popoverPosition}
            onBlur={scheduleClose}
            onFocus={openPopover}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closePopover();
                triggerRef.current?.focus();
              }
            }}
            onPointerEnter={openPopover}
            onPointerLeave={scheduleClose}
          >
            {showRejection ? (
              <div className="approval-status-popover__rejection">
                <div className="approval-status-popover__rejection-title">
                  SKU {resolvedStatus.label} rejected
                </div>
                <div className="approval-status-popover__rejection-details">
                  <p>
                    Your SKU order request was rejected by the{" "}
                    {getRejectionRoleLabel(RejectionReasonInfo?.RoleCd)}{" "}
                    {RejectionReasonInfo?.Name || "--"} (
                    {RejectionReasonInfo?.Email || "--"}).
                  </p>
                  <p>
                    <strong>Reason provided:</strong> “
                    {RejectionReasonInfo?.RejectReason?.trim()}”
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="approval-status-popover__header">
                  <span>{approverLabel} approvers</span>
                  <MotifIconButton
                    type="button"
                    className="approval-status-popover__copy-button"
                    aria-label="Copy all approver emails"
                    title="Copy all approver emails"
                    disabled={allEmails.length === 0}
                    onClick={() => handleCopy(allEmails.join("; "), "all")}
                  >
                    <MotifIcon
                      iconFunction={
                        copiedKey === "all" ? IconoirCheck : IconoirCopy
                      }
                      size="20"
                    />
                  </MotifIconButton>
                </div>
                <div className="approval-status-popover__divider" />
                <div className="approval-status-popover__list">
                  {visibleApprovers.length ? (
                    visibleApprovers.map((approver, index) => {
                      const email = approver.Email?.trim() || "";
                      const key = `${email}-${index}`;
                      return (
                        <div className="approval-status-popover__row" key={key}>
                          <div className="approval-status-popover__details">
                            <div className="approval-status-popover__name">
                              {approver.Name || "--"}
                            </div>
                            <div className="approval-status-popover__email">
                              {email || "--"}
                            </div>
                          </div>
                          <MotifIconButton
                            type="button"
                            className="approval-status-popover__copy-button"
                            aria-label={`Copy ${approver.Name || "approver"}'s email`}
                            title={`Copy ${approver.Name || "approver"}'s email`}
                            disabled={!email}
                            onClick={() => handleCopy(email, key)}
                          >
                            <MotifIcon
                              iconFunction={
                                copiedKey === key ? IconoirCheck : IconoirCopy
                              }
                              size="20"
                            />
                          </MotifIconButton>
                        </div>
                      );
                    })
                  ) : (
                    <div className="approval-status-popover__empty">
                      No approvers assigned
                    </div>
                  )}
                </div>
              </>
            )}
            <span className="approval-status-popover__arrow" aria-hidden />
          </div>,
          document.body,
        )}
    </>
  );
};

export default ApprovalStatusPopover;
